import { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Megaphone, Plus, Pencil, Trash2, Eye, EyeOff, Upload, FileText, Image } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import RichTextEditor from '../components/RichTextEditor';

const AdminAnnouncementsPage = () => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    title: '', content_type: 'text', content: '', image_url: '',
    start_date: '', end_date: '', max_views_per_day: 2,
  });

  useEffect(() => { loadAnnouncements(); }, []);

  const loadAnnouncements = async () => {
    try {
      const res = await api.get('/admin/announcements');
      setAnnouncements(res.data);
    } catch { toast.error('فشل تحميل الإعلانات'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({ title: '', content_type: 'text', content: '', image_url: '', start_date: '', end_date: '', max_views_per_day: 2 });
    setEditing(null);
  };

  const openNew = () => { resetForm(); setShowForm(true); };
  const openEdit = (ann) => {
    setForm({
      title: ann.title, content_type: ann.content_type, content: ann.content || '',
      image_url: ann.image_url || '', start_date: ann.start_date, end_date: ann.end_date,
      max_views_per_day: ann.max_views_per_day,
    });
    setEditing(ann);
    setShowForm(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload/document', fd);
      const fileUrl = `/api/files/${res.data.path}`;
      setForm(prev => ({ ...prev, image_url: fileUrl, content_type: 'image' }));
      toast.success('تم رفع الصورة');
    } catch { toast.error('فشل رفع الصورة'); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.title.trim()) { toast.error('أدخل عنوان الإعلان'); return; }
    if (!form.start_date || !form.end_date) { toast.error('حدد مدة الإعلان'); return; }
    if (form.content_type === 'text' && !form.content.trim()) { toast.error('أدخل محتوى الإعلان'); return; }
    if (form.content_type === 'image' && !form.image_url) { toast.error('ارفع صورة الإعلان'); return; }
    try {
      if (editing) {
        await api.put(`/admin/announcements/${editing.id}`, form);
        toast.success('تم تحديث الإعلان');
      } else {
        await api.post('/admin/announcements', form);
        toast.success('تم إنشاء الإعلان');
      }
      setShowForm(false);
      resetForm();
      loadAnnouncements();
    } catch { toast.error('فشل حفظ الإعلان'); }
  };

  const toggleActive = async (ann) => {
    try {
      await api.put(`/admin/announcements/${ann.id}`, { is_active: !ann.is_active });
      loadAnnouncements();
    } catch { toast.error('فشل تحديث الحالة'); }
  };

  const deleteAnn = async (ann) => {
    if (!window.confirm('هل تريد حذف هذا الإعلان؟')) return;
    try {
      await api.delete(`/admin/announcements/${ann.id}`);
      toast.success('تم الحذف');
      loadAnnouncements();
    } catch { toast.error('فشل الحذف'); }
  };

  const isExpired = (ann) => ann.end_date < new Date().toISOString().split('T')[0];

  if (loading) return <div className="flex items-center justify-center min-h-[40vh]"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-announcements-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-accent flex items-center gap-2"><Megaphone className="w-6 h-6" /> إدارة الإعلانات</h2>
        <Button onClick={openNew} className="gap-2" data-testid="new-announcement-btn"><Plus className="w-4 h-4" /> إعلان جديد</Button>
      </div>

      {announcements.length === 0 && <Card className="p-12 text-center text-muted-foreground">لا توجد إعلانات بعد</Card>}

      <div className="space-y-4">
        {announcements.map(ann => (
          <Card key={ann.id} className={`p-5 ${!ann.is_active ? 'opacity-60' : ''}`} data-testid={`announcement-${ann.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <h3 className="font-bold text-lg text-accent">{ann.title}</h3>
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${ann.is_active && !isExpired(ann) ? 'bg-green-50 text-green-600 border-green-200' : 'bg-red-50 text-red-600 border-red-200'}`}>
                    {!ann.is_active ? 'معطل' : isExpired(ann) ? 'منتهي' : 'نشط'}
                  </span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">
                    {ann.content_type === 'image' ? 'صورة' : 'نص'}
                  </span>
                </div>
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  <span>من: {ann.start_date}</span>
                  <span>إلى: {ann.end_date}</span>
                  <span>الظهور: {ann.max_views_per_day} مرة/يوم</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Button variant="ghost" size="icon" onClick={() => toggleActive(ann)} title={ann.is_active ? 'تعطيل' : 'تفعيل'} data-testid={`toggle-${ann.id}`}>
                  {ann.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button variant="ghost" size="icon" onClick={() => openEdit(ann)} data-testid={`edit-${ann.id}`}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => deleteAnn(ann)} className="text-red-500" data-testid={`delete-${ann.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-2xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'تعديل الإعلان' : 'إعلان جديد'}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-semibold mb-1">عنوان الإعلان</label>
              <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary/30" placeholder="عنوان الإعلان" data-testid="ann-title-input" />
            </div>

            {/* Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-1">تاريخ البداية</label>
                <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg" data-testid="ann-start-date" />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">تاريخ الانتهاء</label>
                <input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg" data-testid="ann-end-date" />
              </div>
            </div>

            {/* Max views */}
            <div>
              <label className="block text-sm font-semibold mb-1">عدد مرات الظهور في اليوم</label>
              <input type="number" min="1" max="20" value={form.max_views_per_day} onChange={e => setForm({ ...form, max_views_per_day: parseInt(e.target.value) || 1 })}
                className="w-full px-3 py-2 border rounded-lg" data-testid="ann-max-views" />
            </div>

            {/* Content type */}
            <div>
              <label className="block text-sm font-semibold mb-2">نوع المحتوى</label>
              <div className="flex gap-3">
                <button type="button" onClick={() => setForm({ ...form, content_type: 'text' })}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-semibold transition-all ${form.content_type === 'text' ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 text-gray-500'}`}
                  data-testid="ann-type-text">
                  <FileText className="w-5 h-5" /> نص (محرر احترافي)
                </button>
                <button type="button" onClick={() => setForm({ ...form, content_type: 'image' })}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-semibold transition-all ${form.content_type === 'image' ? 'border-primary bg-primary/5 text-primary' : 'border-gray-200 text-gray-500'}`}
                  data-testid="ann-type-image">
                  <Image className="w-5 h-5" /> صورة
                </button>
              </div>
            </div>

            {/* Content */}
            {form.content_type === 'text' ? (
              <div>
                <label className="block text-sm font-semibold mb-1">محتوى الإعلان</label>
                <RichTextEditor value={form.content} onChange={val => setForm({ ...form, content: val })} />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-semibold mb-1">صورة الإعلان</label>
                {form.image_url ? (
                  <div className="relative">
                    <img src={form.image_url} alt="إعلان" className="w-full rounded-lg border max-h-80 object-contain" />
                    <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} className="absolute top-2 left-2">تغيير</Button>
                  </div>
                ) : (
                  <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 transition-colors">
                    {uploading ? <div className="animate-spin h-8 w-8 border-2 border-primary rounded-full border-t-transparent mx-auto" /> :
                      <><Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" /><p className="text-gray-500 font-medium">اضغط لرفع صورة</p></>}
                  </div>
                )}
                <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.gif,.webp" className="hidden" onChange={handleImageUpload} />
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button onClick={submit} className="flex-1" data-testid="save-announcement-btn">{editing ? 'تحديث' : 'نشر الإعلان'}</Button>
              <Button variant="outline" onClick={() => setShowForm(false)}>إلغاء</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAnnouncementsPage;
